import { z } from "zod";
import { Timestamp } from "../../common";

const EXAMPLE = {
  requestedAt: "2026-10-26T18:04:00.000Z",
  purgeAfter: "2026-11-25T18:04:00.000Z",
  purgeStartedAt: null,
} as const;

export const DeletionTiming = z.enum(["now", "after_grace_period"]).meta({
  id: "DeletionTiming",
  description:
    "`now` purges the account right away. `after_grace_period` schedules the purge 30 days out, leaving the owner time to change their mind.",
});
export type DeletionTiming = z.infer<typeof DeletionTiming>;

export const RequestAccountDeletionBody = z
  .strictObject({
    when: DeletionTiming,
    appleAuthorizationCode: z.string().min(1).optional().meta({
      description:
        "The `authorizationCode` from a Sign in with Apple sheet the iOS app just showed, for an account with Apple connected. Pax exchanges it for a refresh token, so the purge can revoke the account's Sign in with Apple. Codes expire after five minutes.",
    }),
  })
  .meta({
    id: "RequestAccountDeletionBody",
    description: "When to purge the account, and for iOS, a fresh Apple authorization.",
    example: { when: "after_grace_period" },
  });
export type RequestAccountDeletionBody = z.infer<typeof RequestAccountDeletionBody>;

export const AccountDeletion = z
  .strictObject({
    requestedAt: Timestamp.meta({
      description: "When the owner asked to delete the account.",
      example: EXAMPLE.requestedAt,
    }),
    purgeAfter: Timestamp.meta({
      description:
        "When the account is due to be purged. Its purge starts then, or as soon as a renewing App Store subscription is set not to renew.",
      example: EXAMPLE.purgeAfter,
    }),
    purgeStartedAt: Timestamp.nullable().meta({
      description:
        "When the purge started on the account, after which it can't be cancelled. Null while the owner can still cancel.",
      example: EXAMPLE.purgeStartedAt,
    }),
  })
  .meta({
    id: "AccountDeletion",
    description:
      "A deletion the account's owner asked for, which they can cancel until its purge starts.",
    example: EXAMPLE,
  });
export type AccountDeletion = z.infer<typeof AccountDeletion>;

export const AccountDeletionResponse = z
  .strictObject({
    deletion: AccountDeletion.nullable().meta({
      description: "The pending deletion, or null when the account is not being deleted.",
    }),
  })
  .meta({
    id: "AccountDeletionResponse",
    description: "Whether the caller's account is waiting to be purged.",
    example: { deletion: EXAMPLE },
  });
export type AccountDeletionResponse = z.infer<typeof AccountDeletionResponse>;
